import { TreeView, TraitLabel, NodeId, TreeViewNode, SharedTree } from '@fluid-experimental/tree';
import { MfsItemId } from '../..';
import { assertPrecondition, mfsError, Result } from '../../errors';
import { assertGetValue, logCustomMetric, logVerbose } from '../../log';
import { isCollectionNode, isIndexNode, isItemNode, isReferenceNode } from './typeGuards';
import { MfsCollectionNode, MfsItemNode, MfsReferenceNode, NodeTraits, NodeTypeGuard } from './types';

/** Gets the nodes under a given trait label from the specified parent, or the view's root. */
export function getIndexedNodes(view: TreeView, index: TraitLabel, parent?: NodeId): readonly NodeId[] {
  return view.getTrait({
    parent: parent ?? view.root,
    label: index,
  });
}

/** Get the traits that match the criteria defined by the filter function. */
export function getNodeTraits(
  view: TreeView,
  sourceNodeId: NodeId,
  filter: (traitLabel: TraitLabel) => boolean
): NodeTraits {
  logVerbose('getNodeTraits', `Getting node traits`, { sourceNodeId });
  const sourceNode = view.getViewNode(sourceNodeId);
  const results = new Map<TraitLabel, readonly NodeId[]>();

  sourceNode.traits.forEach((value, key) => {
    if (filter(key)) {
      results.set(key, value);
    }
  });

  return results;
}

/**
 * @returns The node with the given id, if it exists; otherwise, undefined.
 * @throws If the node is not of the expected type.
 */
export function tryGetTypedNode<T extends TreeViewNode>(
  view: TreeView,
  nodeId: NodeId,
  isType: NodeTypeGuard<T>
): T | undefined {
  const node = view.tryGetViewNode(nodeId);

  if (node === undefined) {
    logVerbose('tryGetTypedNode', `Node is undefined`, { nodeId });
    return undefined;
  }

  assertPrecondition(isType(node));
  return node;
}

/**
 * @returns The node with the given id.
 * @throws If the node does not exist, or is not of the expected type.
 */
export function getTypedNode<T extends TreeViewNode>(view: TreeView, nodeId: NodeId, isType: NodeTypeGuard<T>): T {
  const node = view.getViewNode(nodeId);
  assertPrecondition(isType(node));
  return node;
}

/**
 * @returns The item node id for the given MfsItem id, if it exists in the index; otherwise, undefined.
 * @throws If the indexed item node id does not pertain to an existing node.
 */
export function tryGetItemNodeId(tree: SharedTree, view: TreeView, itemId: MfsItemId): NodeId | undefined {
  // read indexed item node id

  const indexNodeId = view.getTrait({ parent: view.root, label: itemId as unknown as TraitLabel })[0];

  if (!indexNodeId) {
    logVerbose('tryGetItemNodeId', `itemId: ${itemId} is not found in the index.`);
    return undefined;
  }

  const itemStableNodeId = getTypedNode(view, indexNodeId, isIndexNode).payload;
  let itemNodeId: NodeId;

  // The Harmony team's webpack bundling modifies the functionality of the convertToStableNodeId function, resulting in a mangled ID. The code below verifies that the stableNodeId generated is not mangled. If it is, we log the error to our telemetry and throw an mfsError to prevent the mangled ID from entering the shared tree.
  try {
    itemNodeId = tree.convertToNodeId(itemStableNodeId);
  } catch {
    logCustomMetric(
      'invalidStableNodeId',
      'invalidStableNodeId',
      { invalidStableNodeId: itemStableNodeId },
      'tryGetItemNodeId'
    );
    throw mfsError.invalidStableNodeId(itemStableNodeId);
  }

  if (itemNodeId) {
    // if there's an indexed item-node, ensure that it exists.
    assertPrecondition(view.hasNode(itemNodeId), () => mfsError.indexCorruption(itemId, JSON.stringify(itemNodeId)));
  }

  logVerbose(
    'tryGetItemNodeId',
    itemNodeId ? `resolved itemId: ${itemId} -> itemNodeId: ${itemNodeId}` : `not found itemId: ${itemId}`
  );
  return itemNodeId;
}

/**
 * @returns The item node id for the given MfsItem id.
 * @throws If the item node id is not found in the index, or the indexed item node id does not pertain to an existing node.
 */
export function getItemNodeId(tree: SharedTree, view: TreeView, itemId: MfsItemId): NodeId {
  const itemNodeId = tryGetItemNodeId(tree, view, itemId);
  assertPrecondition(!!itemNodeId, () => mfsError.itemNotFound(itemId));
  return itemNodeId;
}

/**
 * @returns The node with the given id, if it's a valid item node.
 * @throws If the given id is not found, is not an MfsItemNode id.
 */
export function getItemNode(tree: SharedTree, view: TreeView, itemId: MfsItemId): MfsItemNode {
  return assertGetValue(() => tryGetItemNode(tree, view, itemId));
}

/** @returns The snapshot node with the given id, if any; otherwise throws. */
export function getCollectionNode(tree: SharedTree, view: TreeView, itemId: MfsItemId): MfsCollectionNode {
  return assertGetValue(() => tryGetCollectionNode(tree, view, itemId));
}

/**
 * @returns Success result containing the `MfsItemNode` with the given `id` if it exists; otherwise failure result.
 * @throws If the given id is not an MfsItemNode id.
 */
export function tryGetItemNode(tree: SharedTree, view: TreeView, itemId: MfsItemId): Result<MfsItemNode> {
  const itemNodeId = tryGetItemNodeId(tree, view, itemId);

  if (!itemNodeId) {
    logVerbose('tryGetItemNode', `Failed to find item node`, { itemId });
    return Result.fail(mfsError.itemNotFound(itemId));
  }
  const itemNode = getTypedNode(view, itemNodeId, isItemNode);

  return Result.ok(itemNode);
}

/**
 * @returns Success result containing the node with the given `id`, if it exists, is an MfsCollectionNode otherwise
 *   failure result.
 * @throws If the given id is not an MfsCollectionNode id.
 */
export function tryGetCollectionNode(tree: SharedTree, view: TreeView, itemId: MfsItemId): Result<MfsCollectionNode> {
  const itemNodeResult = tryGetItemNode(tree, view, itemId);

  if (itemNodeResult.isOK === false) {
    logVerbose('tryGetCollectionNode', `Failed to find collection node`, { itemId });
    return itemNodeResult;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const maybeCollectionNode = itemNodeResult.value!;

  if (!isCollectionNode(maybeCollectionNode)) {
    logVerbose('tryGetCollectionNode', `Found node but was not a collection node`, { itemId });
    return Result.fail(mfsError.notCollectionItem(itemId));
  }

  return Result.ok(maybeCollectionNode);
}

/**
 * @returns The {@link MfsReferenceNode} with the given id, if it exists.
 * @throws If the given id is not an MfsReferenceNode id.
 */
export function tryGetReferenceNode(view: TreeView, id: NodeId): MfsReferenceNode | undefined {
  return tryGetTypedNode(view, id, isReferenceNode);
}

/**
 * @returns Success if the item with the given id exists; failure, otherwise.
 * @throws If the given id is not an MfsItemNode id.
 */
export function itemExists(tree: SharedTree, view: TreeView, id: MfsItemId): boolean {
  const itemNodeId = tryGetItemNodeId(tree, view, id);
  return itemNodeId !== undefined;
}
